import { z } from 'zod'
import { ackEnvelope, assertAck, resultEnvelope } from './envelope'
import type { ApiClient } from './client'

/**
 * Data sources — GET /{clusterId}/mgmt/core/device-management/
 *
 * Shape captured live from the gotham scope of the ap1 tenant on 2026-08-21
 * (807 rows), not guessed. What that capture established:
 *
 * - The envelope is `{ TStamp, count, message, status, data: { result: [] } }`.
 *   Note this is NOT the `{ data }` envelope the auth endpoints use, and
 *   `count` is the FULL total (807) while `result` holds only the page.
 * - `status` has exactly two values: 'active' and 'inactive'. The legacy screen
 *   showed four (active/silent/error/disabled); the other two are not in this
 *   payload and must not be synthesised from timestamps.
 * - `device_address` is the only identity, and it was unique across all 807
 *   rows — so it is safe as a row key.
 * - `extractor` and `extractor_id` are PARALLEL arrays, observed lengths 0–34.
 *   707 of 807 rows have none at all, so "no extractor" is the common case,
 *   not an error.
 * - `extractor_id` is NOT always an array: the training scope (2026-09-29)
 *   returned a bare string on some rows. A string is read as one id; `''` and
 *   `null` as no extractor.
 * - `stream` is an array too, observed lengths 1–50; never empty in the capture,
 *   but defaulted here rather than assumed.
 * - Timestamps are FLOAT epoch SECONDS (e.g. 1713895549.6822734), so they need
 *   ×1000 for a JS Date. `last_modified_tstamp` and `description` are often null.
 *
 * Fields the legacy Sources screen displayed that this endpoint does NOT
 * carry: events-per-second, an EPS trend series, the collector name, a
 * human-friendly device name distinct from its address, and a single resolved
 * parser. They need a different endpoint; they are not derivable from here.
 */

export const deviceStatus = z.enum(['active', 'inactive'])
export type DeviceStatus = z.infer<typeof deviceStatus>

export const sourceDevice = z
  .object({
    device_address: z.string(),
    scope_id: z.string(),
    /** 'active' | 'inactive'. Kept permissive so an added value cannot blank the page. */
    status: z.string(),
    description: z.string().nullable().default(null),
    extractor: z.array(z.string()).default([]),
    /** A lone string is ONE id (never split); '' and null read as none. */
    extractor_id: z
      .union([z.array(z.string()), z.string(), z.null()])
      .default([])
      .transform((v) => (v === null || v === '' ? [] : typeof v === 'string' ? [v] : v)),
    stream: z.array(z.string()).default([]),
    /** Float epoch seconds. */
    create_tstamp: z.number().nullable().default(null),
    last_seen_tstamp: z.number().nullable().default(null),
    last_modified_tstamp: z.number().nullable().default(null),
  })
  .passthrough()

/** Shared list envelope; here `count` is the total across ALL pages. */
export const sourcesResponse = resultEnvelope(sourceDevice)

export type SourceDevice = z.output<typeof sourceDevice>

export interface SourcesPage {
  /** This page of devices. */
  devices: SourceDevice[]
  /** Total matching devices across all pages. */
  total: number
}

export interface SourcesQuery {
  clusterId: string
  scopeId: string
  /** 1-based. */
  pageNo?: number
  pageSize?: number
}

/**
 * `DELETE /{clusterId}/mgmt/core/device-management/{scope_id}/{device_address}`
 * — the legacy console's device delete (saas-console-docs/architecture/
 * services-api.md, openapi `devicemgmt_apis_deleteDevice`). The response is
 * documented only as the standard envelope; UNTESTED until capture C1 lands
 * (docs/fabric-overview-requirements.md §Capture ledger), so callers keep the
 * live write gated off.
 */
export interface SourceRemoveInput {
  clusterId: string
  scopeId: string
  deviceAddress: string
}

export interface SourcesApi {
  list: (q: SourcesQuery) => Promise<SourcesPage>
  remove: (q: SourceRemoveInput) => Promise<void>
}

export function createSourcesApi(client: ApiClient): SourcesApi {
  return {
    list: async ({ clusterId, scopeId, pageNo = 1, pageSize = 100 }) => {
      const qs = new URLSearchParams({
        scope_id: scopeId,
        pageno: String(pageNo),
        pagesize: String(pageSize),
      })
      const res = await client.request(
        'GET',
        `/${clusterId}/mgmt/core/device-management/?${qs.toString()}`,
        { schema: sourcesResponse },
      )
      return { devices: res.data.result, total: res.count }
    },
    remove: async ({ clusterId, scopeId, deviceAddress }) => {
      // Both segments are encoded: an IPv6 address carries ':' and a device
      // name may carry '/', which would otherwise split the path.
      const res = await client.request(
        'DELETE',
        `/${clusterId}/mgmt/core/device-management/${encodeURIComponent(scopeId)}/${encodeURIComponent(deviceAddress)}`,
        { schema: ackEnvelope },
      )
      assertAck(res, 'Deleting the device')
    },
  }
}

/* ------------------------------------------------------------------ helpers */

/** Float epoch seconds -> Date. Null-safe, because these fields are often null. */
export function tstampToDate(t: number | null | undefined): Date | null {
  return typeof t === 'number' && Number.isFinite(t) ? new Date(t * 1000) : null
}

/** Compact relative age, e.g. "3m", "2h", "6d". Null when there is no timestamp. */
export function relativeAge(t: number | null | undefined, now: number = Date.now()): string | null {
  const d = tstampToDate(t)
  if (!d) return null
  const secs = Math.max(0, Math.round((now - d.getTime()) / 1000))
  if (secs < 60) return `${secs}s`
  if (secs < 3600) return `${Math.round(secs / 60)}m`
  if (secs < 86_400) return `${Math.round(secs / 3600)}h`
  return `${Math.round(secs / 86_400)}d`
}
